import React, { AnchorHTMLAttributes, ElementType } from 'react';

import cn from 'classnames';

import { usePlaceholderImage } from '../../../helpers/getPlaceholderImage';

export interface CardProps {
  title: React.ReactNode;
  header: React.ReactNode;
  footer: React.ReactNode;
  image?: string;
  url?: string;
  titleText?: string;
  theme?: 'white' | 'black';
  width?: '1x' | '2x' | 'full';
  innerPadding?: boolean;
  bookmark?: React.ReactNode;
  outline?: boolean;
  modifier?: string;
  cardId?: string;
  withFallbackImage?: boolean;
}

function Card(props: CardProps) {
  const {
    outline = true,
    innerPadding = true,
    width = '1x' /* available options: 1x / 2x */,
    title,
    titleText,
    image,
    header,
    footer,
    url,
    bookmark = null,
    theme = 'black' /* available options: black / white */,
    modifier,
    cardId,
    withFallbackImage = false,
  } = props;

  // Set backup placeholder image if image is not available.
  const placeholderImage = usePlaceholderImage({ id: cardId || '' });
  let imageSrc = image;
  if (withFallbackImage && !image) {
    imageSrc = placeholderImage;
  }
  if (withFallbackImage && !cardId) {
    // eslint-disable-next-line no-console
    console.error("Use of 'withFallbackImage' prop requires a 'cardId'.");
  }

  const TagCard: ElementType = url && !bookmark && !footer ? 'a' : 'div';

  // If both bookmark and url are set then set the url on image and header instead on the entire card.
  let TagImgWrapper: ElementType = 'div';
  const tagImgWrapperProps: AnchorHTMLAttributes<unknown> = {
    className: 'auo-card__img',
  };

  const titleString =
    titleText || typeof title === 'string' ? (title as string) : '';

  let titleHtml = (
    <h2 className="au-pl-0" title={titleString || undefined}>
      {title}
    </h2>
  );

  if (url && (bookmark || footer)) {
    TagImgWrapper = 'a';
    tagImgWrapperProps.href = url;

    titleHtml = <a href={url}>{titleHtml}</a>;
  }

  const optimizeImageSrc = (src?: string) => {
    if (!src) return src;
    if (src.includes('//images.ctfassets.net')) {
      const hasQuery = src.includes('?');
      const separator = hasQuery ? '&' : '?';
      return `${src}${separator}w=600`;
    }
    return src;
  };

  return (
    <TagCard
      className={cn(`auo-card auo-card-${theme} auo-card--w-${width} au-flex`, {
        [`auo-card--${modifier}`]: modifier,
        'auo-card__outline': outline,
        'auo-card__inner-padding au-p-6': innerPadding,
        [`lg:au-col-span-2`]: width === '2x',
        [`lg:au-col-span-full`]: width === 'full',
      })}
      href={TagCard === 'a' && url ? url : undefined}
    >
      {!modifier && imageSrc && (
        <TagImgWrapper {...tagImgWrapperProps}>
          <img src={optimizeImageSrc(imageSrc)} alt={titleString} />{' '}
        </TagImgWrapper>
      )}
      <div className="auo-card__content">
        <div className="auo-card__head">
          <div className="auo-card__headtop">
            {header && (
              <div className="auo-card__headtop-content">{header}</div>
            )}
            {bookmark && (
              <span className="auo-bookmark" aria-label="bookmark">
                {bookmark}
              </span>
            )}
          </div>
          {titleHtml}
        </div>
        <div className="auo-card__footer">
          {footer && typeof footer === 'object' && (
            <div className="auo-card__footer-content">{footer}</div>
          )}
          {footer && typeof footer === 'string' && (
            <div
              className="auo-card__footer-content"
              dangerouslySetInnerHTML={{ __html: footer }}
            />
          )}
        </div>
      </div>
      {modifier === 'industry' && imageSrc && (
        <TagImgWrapper {...tagImgWrapperProps}>
          <img src={optimizeImageSrc(imageSrc)} alt={titleString} />{' '}
        </TagImgWrapper>
      )}
    </TagCard>
  );
}

export default Card;
